/**
 * src/components/student-dashboard-components/theme/components/Card.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, createTransition } from '../themeUtils';

const StyledCard = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${getColor('neutral.medium')};
  overflow: hidden;
  position: relative;
  width: 100%;
  max-width: 100%;
  ${createTransition('all', 'normal')};
  
  ${props => props.$variant === 'default' && `
    box-shadow: ${props.theme.shadows.sm};
    
    &:hover {
      box-shadow: ${props.theme.shadows.md};
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.$variant === 'elevated' && `
    box-shadow: ${props.theme.shadows.lg};
    
    &:hover {
      box-shadow: ${props.theme.shadows.xl};
      transform: translateY(-4px);
    }
  `}
  
  ${props => props.$variant === 'flat' && `
    box-shadow: none;
    border: 2px solid ${getColor('neutral.medium')};
    
    &:hover {
      border-color: ${getColor('primary.skyBlue')};
    }
  `}
  
  ${props => props.$variant === 'gradient' && `
    background: ${props.theme.colors.gradients.card};
    box-shadow: ${props.theme.shadows.md};
    
    &:hover {
      box-shadow: ${props.theme.shadows.lg};
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.$interactive && `
    cursor: pointer;
    
    &:active {
      transform: translateY(0) scale(0.98);
    }
  `}
  
  ${props => props.$padding === 'none' && `padding: 0;`}
  ${props => props.$padding === 'small' && `padding: ${props.theme.spacing.md};`}
  ${props => props.$padding === 'medium' && `padding: ${props.theme.spacing.lg};`}
  ${props => props.$padding === 'large' && `padding: ${props.theme.spacing.xl};`}
  ${props => !props.$padding && `padding: ${props.theme.spacing.lg};`}
  
  ${props => props.theme.isMobile && `
    border-radius: ${props.theme.borderRadius.md};
    padding: ${props.$padding === 'large' ? props.theme.spacing.lg : 
              props.$padding === 'small' ? props.theme.spacing.sm :
              props.theme.spacing.md};
    box-sizing: border-box;
  `}
`;

const CardHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${getColor('neutral.medium')};
  background: ${getColor('neutral.light')};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
  `}
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h3.fontSize : 
    props.theme.typography.desktop.h3.fontSize};
  font-weight: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h3.fontWeight : 
    props.theme.typography.desktop.h3.fontWeight};
  line-height: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h3.lineHeight : 
    props.theme.typography.desktop.h3.lineHeight};
  color: ${getColor('primary.blue')};
`;

const CardSubtitle = styled.p`
  margin: ${props => props.theme.spacing.xs} 0 0 0;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.small.fontSize : 
    props.theme.typography.desktop.small.fontSize};
  color: ${getColor('neutral.dark')};
`;

const CardContent = styled.div`
  padding: ${props => props.$noPadding ? '0' : props.theme.spacing.lg};
  
  ${props => props.theme.isMobile && `
    padding: ${props.$noPadding ? '0' : props.theme.spacing.md};
  `}
`;

const CardFooter = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${getColor('neutral.medium')};
  background: ${getColor('neutral.light')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
    flex-direction: column;
    gap: ${props.theme.spacing.sm};
  `}
`;

/**
 * Card.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Card = ({
  children,
  variant = 'default',
  padding,
  interactive = false,
  onClick,
  className,
  ...props
}) => {
  const theme = useTheme();
  
  return (
    <StyledCard
      theme={theme}
      $variant={variant}
      $padding={padding}
      $interactive={interactive}
      onClick={onClick}
      className={className}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

/**
 * Card Header Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const CardHeaderComponent = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <CardHeader theme={theme} className={className} {...props}>
      {children}
    </CardHeader>
  );
};

/**
 * Card Title Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const CardTitleComponent = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <CardTitle theme={theme} className={className} {...props}>
      {children}
    </CardTitle>
  );
};

/**
 * Card Subtitle Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const CardSubtitleComponent = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <CardSubtitle theme={theme} className={className} {...props}>
      {children}
    </CardSubtitle>
  );
};

/**
 * Card Content Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const CardContentComponent = ({ children, noPadding = false, className, ...props }) => {
  const theme = useTheme();
  return (
    <CardContent theme={theme} $noPadding={noPadding} className={className} {...props}>
      {children}
    </CardContent>
  );
};

/**
 * Card Footer Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const CardFooterComponent = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <CardFooter theme={theme} className={className} {...props}>
      {children}
    </CardFooter>
  );
};

Card.Header = CardHeaderComponent;
Card.Title = CardTitleComponent;
Card.Subtitle = CardSubtitleComponent;
Card.Content = CardContentComponent;
Card.Footer = CardFooterComponent;

export default Card;
